import { injectable } from "inversify";
import merge from "lodash/merge";
import { PersonalizerImpl } from "./Personalizer";
import { isolatedEval } from "../../utils/isolatedEval";

export interface AnimationPersonalizer {
  personalizeAnim(animationJSON: any, customAttributes: any, personalizationJSON: any): any;
}

@injectable()
export class AnimationPersonalizerImpl extends PersonalizerImpl implements AnimationPersonalizer {

  personalizeAnim(animationJSON: any, customAttributes: { [attr: string]: string }, personalizationJSON: any): any {

    if (customAttributes) {
      this.updateAnimJSON(animationJSON, this.personalize(customAttributes, personalizationJSON));
      return animationJSON;
    }

    return animationJSON;
  }

  private updateAnimJSON(animationItem: any, customAttributes: { [key: string]: string }) {

    const values = Object.keys(customAttributes).map((key) => JSON.parse(this.escapeBreakingChars(customAttributes[key])));

    let globalExpr = "";

    for (const value of values) {
      for (const valItem of value) {
        switch (valItem.type) {
          case "letiant":
            break;
          case "global":
            if (valItem.init) {
              // tslint:disable-next-line:no-eval
              globalExpr += valItem.init + ";";
            }
            break;
          default:
            // appease linter
            break;
        }
      }
    }

    for (const value of values) {
      for (const valItem of value) {
        const layers = this.findLayersByName(animationItem, valItem.name);
        // tslint:disable:prefer-for-of
        for (let i = 0; i < layers.length; i++) {
          if (valItem.position) {
            if (valItem.keyFrameNo === undefined) {
              // tslint:disable-next-line:no-eval
              layers[i].ks.p.k = this.evalMethodSelector(globalExpr + valItem.position);
            } else {
              // tslint:disable-next-line:no-eval
              let evaluatedValue = this.evalMethodSelector(globalExpr + valItem.position);
              evaluatedValue = evaluatedValue.length === 2 ? [...evaluatedValue, 0] : evaluatedValue;
              layers[i].ks.p.k[valItem.keyFrameNo].s = evaluatedValue;
            }
          }
          if (valItem.scale) {
            // tslint:disable-next-line:no-eval
            let evaluatedValue = this.evalMethodSelector(globalExpr + valItem.scale);
            evaluatedValue = typeof evaluatedValue === "number" ? [evaluatedValue, evaluatedValue] : evaluatedValue;
            if (valItem.keyFrameNo === undefined) {
              layers[i].ks.s.k = evaluatedValue;
            } else {
              layers[i].ks.s.k[valItem.keyFrameNo].s = evaluatedValue;
            }
          }
          if (valItem.anchor) {
            // tslint:disable-next-line:no-eval
            if (valItem.keyFrameNo === undefined) {
              // tslint:disable-next-line:no-eval
              layers[i].ks.a.k = this.evalMethodSelector(globalExpr + valItem.anchor);
            } else {
              // tslint:disable-next-line:no-eval
              let evaluatedValue = this.evalMethodSelector(globalExpr + valItem.anchor);
              evaluatedValue = evaluatedValue.length === 2 ? [...evaluatedValue, 0] : evaluatedValue;
              layers[i].ks.a.k[valItem.keyFrameNo].s = evaluatedValue;
            }
          }
          if (valItem.opacity) {
            // tslint:disable-next-line:no-eval
            if (valItem.keyFrameNo === undefined) {
              // tslint:disable-next-line:no-eval
              layers[i].ks.o.k = this.evalMethodSelector(globalExpr + valItem.opacity);
            } else {
              // tslint:disable-next-line:no-eval
              let evaluatedValue = this.evalMethodSelector(globalExpr + valItem.opacity);
              evaluatedValue = evaluatedValue instanceof Array ? evaluatedValue : [evaluatedValue];
              layers[i].ks.o.k[valItem.keyFrameNo].s = evaluatedValue;
            }

          }
          if (valItem.rotation) {
            // tslint:disable-next-line:no-eval
            if (valItem.keyFrameNo === undefined) {
              // tslint:disable-next-line:no-eval
              layers[i].ks.r.k = this.evalMethodSelector(globalExpr + valItem.rotation);
            } else {
              // tslint:disable-next-line:no-eval
              let evaluatedValue = this.evalMethodSelector(globalExpr + valItem.rotation);
              evaluatedValue = evaluatedValue instanceof Array ? evaluatedValue : [evaluatedValue];
              layers[i].ks.r.k[valItem.keyFrameNo].s = evaluatedValue;
            }
          }
        }

        switch (valItem.type) {
          case "text":
            this.updateText(animationItem, valItem, globalExpr);
            break;
          case "shape":
            this.updateShape(animationItem, valItem, globalExpr);
            break;
          case "image":
            // tslint:disable-next-line:no-eval
            this.updateImage(animationItem, valItem, globalExpr);
            break;
          default:
            // appease linter
            break;
        }
      }
    }
  }

  private escapeBreakingChars(parse: string): string {
    return parse.replace(/([^\\])\t/g, "$1\\t");
  }

  private updateText(animationItem: any, value: any, globalExpr: string) {
    const layers = this.findLayersByName(animationItem, value.name);
    // tslint:disable:prefer-for-of
    for (let i = 0; i < layers.length; i++) {
      if (value.val) {
        // tslint:disable-next-line:no-eval
        layers[i].t.d.k[0].s.t = this.evalMethodSelector(globalExpr + value.val);
      }
      if (value.color) {
        layers[i].t.d.k[0].s.fc[0] = parseInt("0x" + value.color.slice(0, 2), 16) / 256;
        layers[i].t.d.k[0].s.fc[1] = parseInt("0x" + value.color.slice(2, 4), 16) / 256;
        layers[i].t.d.k[0].s.fc[2] = parseInt("0x" + value.color.slice(4, 6), 16) / 256;
      }
      if (value.fontSize) {
        // tslint:disable-next-line:no-eval
        layers[i].t.d.k[0].s.s = this.evalMethodSelector(globalExpr + value.fontSize);
      }
      if (value.font) {
        // tslint:disable-next-line:no-eval
        layers[i].t.d.k[0].s.f = this.evalMethodSelector(globalExpr + value.font);
      }
      if (value.init) {
        // tslint:disable-next-line:no-eval
        merge(layers[i], this.evalMethodSelector(globalExpr + value.init));
      }
    }
    // tslint:enable:prefer-for-of
  }

  private updateShape(animationItem: any, value: any, globalExpr: string) {
    const layers = this.findLayersByName(animationItem, value.name);
    for (const layer of layers) {
      if (value.init) {
        // tslint:disable-next-line:no-eval
        merge(layer, this.evalMethodSelector(globalExpr + value.init));
      }
    }
  }

  private updateImage(animationItem: any, value: any, globalExpr: string) {
    const layers = this.findLayersByName(animationItem, value.name);
    for (const layer of layers) {
      if (value.src) {
        // tslint:disable-next-line:no-eval
        const src = this.evalMethodSelector(globalExpr + value.src);
        const asset = this.findAssetByRefId(animationItem, layer.refId);
        const pathLength = src.lastIndexOf("/");
        asset.u = src.substr(0, pathLength) + "/";
        asset.p = src.substr(pathLength + 1, src.length);
      }
    }
  }

  private findLayersByName(animationItem: any, name: string): any[] {
    const layers = [];
    const elements = animationItem.layers;
    for (const element of elements) {
      if (element.nm === name) {
        layers.push(element);
      }
    }
    return layers;
  }

  private findAssetByRefId(animationItem: any, refId: string) {
    let asset = null;
    const elements = animationItem.assets;
    for (const element of elements) {
      if (element.id === refId) {
        asset = element;
        break;
      }
    }
    return asset;
  }

  private evalMethodSelector(expr: string) {
    // tslint:disable-next-line:no-constant-condition
    if (true) {
      // tslint:disable-next-line:no-eval
      return eval(expr);
    } else {
      return isolatedEval(expr);
    }
  }
}
